import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, shortDate } from "../lib/format";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Search, Plus, Image as ImageIcon, Wallet, Tag, CheckSquare, Square, X, Rows3, Calendar, Pin, PinOff, GripVertical } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import HelpButton from "../components/HelpButton";
import MerchantLogo from "../components/MerchantLogo";
import { CategoryPill } from "../lib/categoryIcons";
import { useAccounts } from "../lib/accounts";

const CATEGORY_OPTIONS_FOR_BULK = [
  "Food", "Groceries", "Transport", "Shopping", "Entertainment",
  "Bills", "Subscriptions", "Gifts", "Income", "Other",
];

export default function RegisterPage() {
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Bulk-select state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Filters bar collapse — closed by default so the register shows txns first
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Pin-to-top state — session-local only. Users pin ambiguous transactions
  // while auditing; refreshing / switching accounts clears the list. Order in
  // `pinnedOrder` is the drag-reorder result and doubles as membership.
  const [pinnedOrder, setPinnedOrder] = useState([]);
  const [bulkCategory, setBulkCategory] = useState("Food");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedId, current } = useAccounts();
  // Track prev account id so we only reset filters on a REAL account change,
  // not on useAccounts's initial async "" → real transition. Ref starts as a
  // sentinel so the first fire (whatever selectedId happens to be) skips reset.
  const prevAccount = useRef("__init__");

  // Seed date filter from ?dateFrom= & ?dateTo= on mount
  useEffect(() => {
    const df = searchParams.get("dateFrom");
    const dt = searchParams.get("dateTo");
    if (df) setDateFrom(df);
    if (dt) setDateTo(dt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = async () => {
    if (selectedId === undefined) return;
    const acc = selectedId ? `?account_id=${selectedId}` : "";
    const [t, s] = await Promise.all([
      api.get(`/transactions${acc}`),
      api.get(`/summary${acc}`),
    ]);
    setTxns(t.data);
    setSummary(s.data);
  };

  useEffect(() => {
    reload();
  }, [selectedId]);

  // Reset category filter + selection + date range when the selected account changes.
  // Skips the initial "" → real transition from useAccounts so URL params survive.
  useEffect(() => {
    const prev = prevAccount.current;
    prevAccount.current = selectedId;
    // First observation of any selectedId (sentinel or "" → real): don't reset.
    if (prev === "__init__" || prev === "" || prev === null || prev === undefined) return;
    if (prev === selectedId) return;
    setCategory("all");
    setSelectMode(false);
    setSelectedIds(new Set());
    setDateFrom("");
    setDateTo("");
    setPinnedOrder([]);
  }, [selectedId]);

  // Sorted, deduped category list derived from the current account's txns.
  // Empty/missing categories are grouped under "Uncategorized".
  const categoryOptions = useMemo(() => {
    const seen = new Set();
    txns.forEach((t) => seen.add((t.category || "").trim() || "__empty__"));
    const list = Array.from(seen).sort((a, b) => {
      if (a === "__empty__") return 1;
      if (b === "__empty__") return -1;
      return a.localeCompare(b);
    });
    return list;
  }, [txns]);

  const visible = txns.filter((t) => {
    if (filter === "uncleared" && t.cleared) return false;
    if (filter === "cleared" && !t.cleared) return false;
    if (filter === "deposits" && t.type !== "deposit") return false;
    if (filter === "spend" && t.type !== "withdrawal") return false;
    if (category !== "all") {
      const c = (t.category || "").trim();
      if (category === "__empty__" && c) return false;
      if (category !== "__empty__" && c !== category) return false;
    }
    if (dateFrom && (t.date || "") < dateFrom) return false;
    if (dateTo && (t.date || "") > dateTo) return false;
    if (q) {
      const needle = q.toLowerCase().trim();
      const payee = (t.payee || "").toLowerCase();
      const amt = String(t.amount ?? "");
      if (!payee.includes(needle) && !amt.includes(needle)) return false;
    }
    return true;
  });

  // Split `visible` into pinned (user's drag order) + unpinned (chronological).
  const pinnedSet = useMemo(() => new Set(pinnedOrder), [pinnedOrder]);
  const pinnedVisible = useMemo(() => {
    const byId = new Map(visible.map((t) => [t.id, t]));
    return pinnedOrder.map((id) => byId.get(id)).filter(Boolean);
  }, [pinnedOrder, visible]);
  const unpinnedVisible = useMemo(
    () => visible.filter((t) => !pinnedSet.has(t.id)),
    [visible, pinnedSet]
  );

  // How many filters are actively narrowing the list — surfaced in the
  // "Filters (N)" button label so users know something is hidden.
  const hasDateFilter = !!(dateFrom || dateTo);
  const activeFilterCount =
    (filter !== "all" ? 1 : 0) +
    (category !== "all" ? 1 : 0) +
    (hasDateFilter ? 1 : 0);

  const togglePin = (id) => {
    setPinnedOrder((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id]; // new pins go to the bottom of the pinned stack
    });
  };
  const unpinAll = () => setPinnedOrder([]);
  // framer-motion Reorder gives us the new ordered array of txn objects;
  // extract ids to update our order state.
  const reorderPinned = (newOrder) => {
    setPinnedOrder(newOrder.map((t) => t.id));
  };

  // Filtered totals — cleared deposits/withdrawals/count for the visible range.
  // Powers cycle-vs-bank comparisons on the Reconcile screen.
  const filteredTotals = useMemo(() => {
    let clDep = 0, clWd = 0, unDep = 0, unWd = 0, clN = 0, unN = 0;
    for (const t of visible) {
      const amt = Number(t.amount) || 0;
      if (t.type === "deposit") {
        if (t.cleared) { clDep += amt; clN += 1; }
        else { unDep += amt; unN += 1; }
      } else {
        if (t.cleared) { clWd += amt; clN += 1; }
        else { unWd += amt; unN += 1; }
      }
    }
    return { clDep, clWd, unDep, unWd, clN, unN, total: visible.length };
  }, [visible]);

  // Per-category totals for the currently visible transactions.
  // Withdrawals count positively (money out); deposits are shown separately.
  const categoryTotals = useMemo(() => {
    const map = new Map();
    visible.forEach((t) => {
      const key = (t.category || "").trim() || "Uncategorized";
      const cur = map.get(key) || { spent: 0, earned: 0, count: 0 };
      if (t.type === "deposit") cur.earned += Number(t.amount) || 0;
      else cur.spent += Number(t.amount) || 0;
      cur.count += 1;
      map.set(key, cur);
    });
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v, total: v.spent + v.earned }))
      .sort((a, b) => b.total - a.total);
  }, [visible]);

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    if (selectedIds.size === visible.length && visible.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visible.map((t) => t.id)));
    }
  };

  const exitSelect = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const applyBulkCategory = async () => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/transactions/bulk-update-category", {
        ids: Array.from(selectedIds),
        category: bulkCategory,
      });
      toast.success(`Updated ${data.updated} to ${data.category}`);
      exitSelect();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="register-page">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl font-black">Register</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/transactions/bulk-add"
            data-testid="bulk-add-link"
            className="nb-btn nb-btn-secondary !py-2 !px-3"
            title="Add many transactions at once"
          >
            <Rows3 size={16} strokeWidth={3} />
            <span className="hidden sm:inline">Bulk add</span>
          </Link>
          <HelpButton id="register" />
          {selectMode ? (
            <button
              type="button"
              data-testid="select-cancel-btn"
              onClick={exitSelect}
              className="nb-btn nb-btn-secondary !py-2 !px-3"
            >
              <X size={16} strokeWidth={3} /> Cancel
            </button>
          ) : (
            <button
              type="button"
              data-testid="select-mode-btn"
              onClick={() => setSelectMode(true)}
              className="nb-btn nb-btn-secondary !py-2 !px-3"
              title="Select multiple transactions to re-categorize"
            >
              <CheckSquare size={16} strokeWidth={3} />
              <span className="hidden sm:inline">Select</span>
            </button>
          )}
          <Link to="/add" className="nb-btn nb-btn-dark !py-2 !px-3" data-testid="new-txn-btn">
            <Plus size={18} strokeWidth={3} /> New
          </Link>
        </div>
      </div>

      {/* Account balance summary */}
      {summary && (
        <div
          className="nb-card-lg p-4"
          data-testid="register-balance-card"
          style={{ background: current?.color || "#fff" }}
        >
          <div className="flex items-center gap-2 mb-1">
            <Wallet size={14} strokeWidth={3} />
            <div className="label !mb-0">{current?.name || "Account"}</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">Register</div>
              <div
                data-testid="register-balance"
                className={`font-display font-black text-xl sm:text-2xl ${
                  summary.register_balance < 0 ? "balance-neg" : ""
                }`}
              >
                {fmtMoney(summary.register_balance)}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">Cleared</div>
              <div
                data-testid="register-cleared"
                className="font-display font-black text-xl sm:text-2xl"
              >
                {fmtMoney(summary.cleared_balance)}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">Pending</div>
              <div
                data-testid="register-pending"
                className={`font-display font-black text-xl sm:text-2xl ${
                  summary.uncleared_total !== 0 ? "" : "opacity-60"
                }`}
              >
                {fmtMoney(summary.uncleared_total)}
              </div>
            </div>
          </div>
          <div className="text-[11px] font-medium text-[#4a4a4a] mt-2">
            {summary.transaction_count} transaction{summary.transaction_count === 1 ? "" : "s"} · starts at{" "}
            {fmtMoney(summary.starting_balance)}
          </div>
        </div>
      )}

      <Link
        to="/duplicates"
        data-testid="find-duplicates-link"
        className="nb-btn nb-btn-secondary w-full"
      >
        📥 Review duplicate inbox
      </Link>

      <div className="nb-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 border-2 border-[#1a1a1a] rounded-xl px-3 py-2 bg-white">
            <Search size={16} strokeWidth={3} />
            <input
              data-testid="txn-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search payee or amount (e.g. 21.85)…"
              className="w-full outline-none font-medium text-sm"
            />
          </div>
          <button
            type="button"
            data-testid="filters-toggle"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`nb-btn ${activeFilterCount > 0 ? "nb-btn-dark" : "nb-btn-secondary"} !py-2 !px-3 text-xs shrink-0`}
            aria-expanded={filtersOpen}
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 text-[10px] font-black tabular-nums">
                ({activeFilterCount})
              </span>
            )}
          </button>
        </div>
        {filtersOpen && (
          <>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {[
            ["all", "All"],
            ["uncleared", "Uncleared"],
            ["cleared", "Cleared"],
            ["deposits", "Deposits"],
            ["spend", "Spend"],
          ].map(([k, l]) => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setFilter(k)}
              className={`chip whitespace-nowrap ${filter === k ? "active" : ""}`}
            >
              {l}
            </button>
          ))}
        </div>
        {categoryOptions.length > 0 && (
          <div
            className="flex items-center gap-2 border-2 border-[#1a1a1a] rounded-xl px-3 py-2 bg-white"
            data-testid="category-filter-row"
          >
            <Tag size={14} strokeWidth={3} />
            <label
              htmlFor="reg-cat-filter"
              className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]"
            >
              Category
            </label>
            <select
              id="reg-cat-filter"
              data-testid="category-filter"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="flex-1 outline-none font-bold text-sm bg-transparent"
            >
              <option value="all">All categories</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c === "__empty__" ? "Uncategorized" : c}
                </option>
              ))}
            </select>
            {category !== "all" && (
              <button
                type="button"
                data-testid="category-filter-clear"
                onClick={() => setCategory("all")}
                className="text-[10px] font-bold underline text-[#4a4a4a]"
                aria-label="Clear category filter"
              >
                Clear
              </button>
            )}
          </div>
        )}
        {/* Date range filter — useful for reconcile cycles (e.g. 6/18 – 7/17) */}
        <div
          className="flex items-center gap-2 border-2 border-[#1a1a1a] rounded-xl px-3 py-2 bg-white"
          data-testid="date-filter-row"
        >
          <Calendar size={14} strokeWidth={3} />
          <label
            htmlFor="reg-date-from"
            className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]"
          >
            Date
          </label>
          <input
            id="reg-date-from"
            data-testid="date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="flex-1 min-w-0 outline-none font-bold text-sm bg-transparent"
            aria-label="Date from"
          />
          <span className="text-[10px] font-bold text-[#4a4a4a]">to</span>
          <input
            data-testid="date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="flex-1 min-w-0 outline-none font-bold text-sm bg-transparent"
            aria-label="Date to"
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              data-testid="date-filter-clear"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
              className="text-[10px] font-bold underline text-[#4a4a4a]"
              aria-label="Clear date filter"
            >
              Clear
            </button>
          )}
        </div>
          </>
        )}
      </div>

      {/* Filtered totals — appears whenever any filter narrows the list. Useful
          when comparing SyncBook's cycle totals to a bank statement. */}
      {(hasDateFilter || filter !== "all" || category !== "all" || q) && (
        <div
          className="nb-card p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center"
          data-testid="filtered-totals"
          style={{ background: "#fdecc8" }}
        >
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
              Cleared Dep
            </div>
            <div
              data-testid="filtered-cleared-deposits"
              className="font-display font-black text-sm balance-pos"
            >
              +{fmtMoney(filteredTotals.clDep)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
              Cleared Wd
            </div>
            <div
              data-testid="filtered-cleared-withdrawals"
              className="font-display font-black text-sm"
            >
              -{fmtMoney(filteredTotals.clWd)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
              Net (cleared)
            </div>
            <div
              data-testid="filtered-cleared-net"
              className={`font-display font-black text-sm ${
                filteredTotals.clDep - filteredTotals.clWd >= 0 ? "balance-pos" : "balance-neg"
              }`}
            >
              {filteredTotals.clDep - filteredTotals.clWd >= 0 ? "+" : "-"}
              {fmtMoney(Math.abs(filteredTotals.clDep - filteredTotals.clWd))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
              Rows
            </div>
            <div
              data-testid="filtered-count"
              className="font-display font-black text-sm"
            >
              {filteredTotals.total}
              {filteredTotals.unN > 0 && (
                <span className="text-[10px] font-bold text-[#4a4a4a] ml-1">
                  ({filteredTotals.unN} pending)
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Per-category totals */}
      {categoryTotals.length > 0 && (
        <div
          className="flex gap-2 overflow-x-auto no-scrollbar pb-1"
          data-testid="category-totals"
        >
          {categoryTotals.map((c) => {
            const isActive = category === c.name || (c.name === "Uncategorized" && category === "__empty__");
            const value = c.spent > c.earned ? c.spent : c.earned;
            const isSpend = c.spent >= c.earned;
            return (
              <button
                key={c.name}
                type="button"
                data-testid={`cat-total-${c.name.toLowerCase()}`}
                onClick={() =>
                  setCategory(
                    isActive ? "all" : c.name === "Uncategorized" ? "__empty__" : c.name
                  )
                }
                className={`nb-card px-3 py-2 flex-shrink-0 min-w-[110px] text-left transition ${
                  isActive ? "!bg-[#1a1a1a] text-[#b5e4ca]" : "hover:brightness-95"
                }`}
              >
                <div
                  className={`text-[10px] font-bold uppercase truncate ${
                    isActive ? "text-[#b5e4ca]/80" : "text-[#4a4a4a]"
                  }`}
                >
                  {c.name}
                </div>
                <div className="font-display font-black text-base">
                  {isSpend ? "-" : "+"}
                  {fmtMoney(value)}
                </div>
                <div
                  className={`text-[10px] font-bold ${
                    isActive ? "text-[#b5e4ca]/80" : "text-[#4a4a4a]"
                  }`}
                >
                  {c.count} txn{c.count === 1 ? "" : "s"}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectMode && visible.length > 0 && (
        <button
          type="button"
          data-testid="select-all-btn"
          onClick={toggleSelectAllVisible}
          className="text-xs font-bold underline text-[#4a4a4a]"
        >
          {selectedIds.size === visible.length && visible.length > 0
            ? "Deselect all"
            : `Select all ${visible.length} visible`}
        </button>
      )}

      {/* Pinned section — appears above the register, drag-to-reorder */}
      {pinnedVisible.length > 0 && (
        <div className="nb-card overflow-hidden" data-testid="pinned-list" style={{ borderColor: "#e0b400" }}>
          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-[#fff7d6] border-b-2 border-[#1a1a1a]/15">
            <div className="flex items-center gap-1.5">
              <Pin size={13} strokeWidth={3} />
              <div className="text-[11px] font-bold uppercase tracking-wider">
                Pinned ({pinnedVisible.length}) · drag to reorder
              </div>
            </div>
            <button
              type="button"
              onClick={unpinAll}
              data-testid="unpin-all-btn"
              className="text-[11px] font-bold underline text-[#4a4a4a]"
            >
              Unpin all
            </button>
          </div>
          <Reorder.Group
            axis="y"
            values={pinnedVisible}
            onReorder={reorderPinned}
            className="list-none pl-0"
          >
            {pinnedVisible.map((t) => (
              <TxnRow
                key={t.id}
                t={t}
                pinned
                checked={selectedIds.has(t.id)}
                selectMode={selectMode}
                onOpen={() => (selectMode ? toggleId(t.id) : nav(`/txn/${t.id}`))}
                onTogglePin={() => togglePin(t.id)}
              />
            ))}
          </Reorder.Group>
        </div>
      )}

      <div className="nb-card overflow-hidden" data-testid="txn-list">
        {visible.length === 0 && (
          <div className="p-6 text-center text-sm font-medium text-[#4a4a4a]">
            Nothing here. Add your first transaction.
          </div>
        )}
        {unpinnedVisible.map((t) => (
          <TxnRow
            key={t.id}
            t={t}
            pinned={false}
            checked={selectedIds.has(t.id)}
            selectMode={selectMode}
            onOpen={() => (selectMode ? toggleId(t.id) : nav(`/txn/${t.id}`))}
            onTogglePin={() => togglePin(t.id)}
          />
        ))}
      </div>

      {/* Bulk action bar — sticky bottom sheet */}
      {selectMode && selectedIds.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 nb-card-lg p-3 flex items-center gap-2 max-w-[95vw]"
          style={{ background: "#1a1a1a", color: "#fdecc8" }}
        >
          <div className="font-display font-black text-sm px-1">
            {selectedIds.size} selected
          </div>
          <select
            data-testid="bulk-category-select"
            value={bulkCategory}
            onChange={(e) => setBulkCategory(e.target.value)}
            className="border-2 border-[#fdecc8] rounded-lg px-2 py-2 font-bold text-sm text-[#1a1a1a] bg-[#fdecc8]"
          >
            {CATEGORY_OPTIONS_FOR_BULK.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="bulk-apply-btn"
            onClick={applyBulkCategory}
            disabled={busy}
            className="nb-btn nb-btn-dark !bg-[#b5e4ca] !text-[#1a1a1a] !py-2 !px-3 !text-sm"
          >
            {busy ? "…" : "Apply"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Single transaction row.
 * - When `pinned=true`, renders as a Reorder.Item with a grip drag handle
 *   (drag only starts from the handle so tapping the row still opens edit).
 * - When `pinned=false`, renders as a plain div (unpinned register keeps its
 *   chronological order).
 * Both modes share layout + pin-toggle button so the row jumps between
 * sections with zero visual regression.
 */
function TxnRow({ t, pinned, checked, selectMode, onOpen, onTogglePin }) {
  const controls = useDragControls();
  const bg = checked
    ? "#fdecc8"
    : pinned
    ? "#fff7d6"
    : !t.cleared
    ? "var(--uncleared)"
    : undefined;

  const inner = (
    <div
      className="w-full flex items-center border-b-2 border-[#1a1a1a]/10 last:border-0"
      style={{ background: bg }}
    >
      {pinned && (
        <button
          type="button"
          onPointerDown={(e) => {
            e.preventDefault();
            controls.start(e);
          }}
          data-testid={`txn-drag-${t.id}`}
          aria-label="Drag to reorder pinned"
          title="Drag to reorder"
          className="touch-none cursor-grab active:cursor-grabbing px-2 py-3 text-[#4a4a4a] hover:text-[#1a1a1a] transition-colors"
        >
          <GripVertical size={16} strokeWidth={3} />
        </button>
      )}
      <button
        type="button"
        data-testid={`txn-row-${t.id}`}
        onClick={onOpen}
        className={`flex-1 min-w-0 flex items-center justify-between py-3 ${pinned ? "pr-2 pl-1" : "px-4"} text-left transition hover:bg-black/5`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {selectMode && (
            <div
              className="flex-shrink-0"
              data-testid={`row-check-${t.id}`}
              aria-checked={checked}
              role="checkbox"
            >
              {checked ? (
                <CheckSquare size={20} strokeWidth={3} />
              ) : (
                <Square size={20} strokeWidth={3} />
              )}
            </div>
          )}
          <div
            className={`w-10 h-10 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center flex-shrink-0 ${
              t.type === "deposit" ? "bg-[#b5e4ca]" : "bg-white"
            }`}
          >
            {t.type === "deposit" ? (
              <ArrowDownRight size={18} strokeWidth={3} />
            ) : (
              <ArrowUpRight size={18} strokeWidth={3} />
            )}
          </div>
          {t.type !== "deposit" && (
            <MerchantLogo payee={t.payee} size={28} />
          )}
          <div className="min-w-0">
            <div className="font-bold flex items-center gap-1.5 truncate">
              {t.payee}
              {t.receipt_image && (
                <ImageIcon size={14} strokeWidth={3} className="opacity-60" />
              )}
            </div>
            <div className="text-xs font-medium text-[#4a4a4a] flex items-center gap-2 flex-wrap">
              <span>{shortDate(t.date)}</span>
              {t.category && <CategoryPill category={t.category} size="xs" />}
            </div>
          </div>
        </div>
        <div
          className={`font-display font-black text-lg ${
            t.type === "deposit" ? "balance-pos" : ""
          }`}
        >
          {t.type === "deposit" ? "+" : "-"}
          {fmtMoney(t.amount)}
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin();
        }}
        data-testid={`pin-btn-${t.id}`}
        aria-label={pinned ? "Unpin transaction" : "Pin transaction to top"}
        title={pinned ? "Unpin" : "Pin to top"}
        className={`shrink-0 px-3 py-3 transition-colors ${pinned ? "text-[#c48b00]" : "text-[#4a4a4a]/40 hover:text-[#4a4a4a]"}`}
      >
        {pinned ? (
          <Pin size={16} strokeWidth={3} fill="currentColor" />
        ) : (
          <Pin size={16} strokeWidth={3} />
        )}
      </button>
    </div>
  );

  if (pinned) {
    return (
      <Reorder.Item
        value={t}
        dragListener={false}
        dragControls={controls}
        as="li"
        className="list-none"
      >
        {inner}
      </Reorder.Item>
    );
  }
  return inner;
}

